import React, { useState, useCallback, useMemo } from 'react';
import { DRAFT_ORDER_2026, getTierLabel, getTierColor, getDraftProspects } from './draftProspects';
import DRAFT_PICKS from './draftPicks';

// ============================================================================
// DRAFT BOARD COMPONENT
// ============================================================================
const AIBadge = () => <span style={{ display: 'inline-block', fontSize: 8, fontWeight: 700, color: 'var(--accent-blue, #4a9eff)', background: 'rgba(74,158,255,0.12)', borderRadius: 3, padding: '1px 4px', marginLeft: 5, verticalAlign: 'middle', letterSpacing: 0.5 }}>AI</span>;

const DraftBoard = ({ salaryData, seasonYear = 2025 }) => {
  const draftYear = (seasonYear || 2025) + 1;
  const prospects = useMemo(() => {
    const raw = getDraftProspects(draftYear);
    if (salaryData) {
      const rostered = new Set();
      for (const abbr of Object.keys(salaryData)) {
        if (salaryData[abbr]?.players) {
          for (const p of salaryData[abbr].players) { if (p.name) rostered.add(p.name); }
        }
      }
      return raw.filter(p => !rostered.has(p.name));
    }
    return raw;
  }, [draftYear, salaryData]);

  const [view, setView] = useState('board'); // 'board' | 'mock' | 'team'
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [posFilter, setPosFilter] = useState('ALL');

  // --- TEAM ABBR -> FULL NAME MAP ---
  const TEAM_NAMES = {
    ATL: 'Atlanta Hawks', BOS: 'Boston Celtics', BKN: 'Brooklyn Nets', CHA: 'Charlotte Hornets',
    CHI: 'Chicago Bulls', CLE: 'Cleveland Cavaliers', DAL: 'Dallas Mavericks', DEN: 'Denver Nuggets',
    DET: 'Detroit Pistons', GSW: 'Golden State Warriors', HOU: 'Houston Rockets', IND: 'Indiana Pacers',
    LAC: 'LA Clippers', LAL: 'Los Angeles Lakers', MEM: 'Memphis Grizzlies', MIA: 'Miami Heat',
    MIL: 'Milwaukee Bucks', MIN: 'Minnesota Timberwolves', NOP: 'New Orleans Pelicans', NYK: 'New York Knicks',
    OKC: 'Oklahoma City Thunder', ORL: 'Orlando Magic', PHI: 'Philadelphia 76ers', PHX: 'Phoenix Suns',
    POR: 'Portland Trail Blazers', SAC: 'Sacramento Kings', SAS: 'San Antonio Spurs', TOR: 'Toronto Raptors',
    UTA: 'Utah Jazz', WAS: 'Washington Wizards',
  };

  // --- FILTER PROSPECTS ---
  const positions = ['ALL', 'PG', 'SG', 'SF', 'PF', 'C'];

  const filteredProspects = prospects.filter(p => {
    if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase()) && !p.school.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (posFilter !== 'ALL' && !p.pos.includes(posFilter)) return false;
    return true;
  });

  // --- GET TEAM PICKS ---
  const getTeamPicks = useCallback((abbr) => {
    const teamData = DRAFT_PICKS[abbr];
    if (!teamData) return [];
    return teamData.picks || [];
  }, []);

  // --- PROSPECT DETAIL MODAL ---
  const ProspectModal = ({ prospect, onClose }) => {
    if (!prospect) return null;
    const tierColor = getTierColor(prospect.tier);

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="prospect-modal" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>×</button>

          <div className="prospect-modal-header">
            <div className="prospect-modal-label">2026 Prospect #{prospect.rank}</div>
            <div className="prospect-modal-name">{prospect.name}{prospect.aiGenerated && <AIBadge />}</div>
            <div className="prospect-modal-meta">{prospect.pos} · {prospect.school} · {prospect.cls}</div>
          </div>

          <div className="prospect-modal-tier" style={{ borderLeftColor: tierColor }}>
            <span className="prospect-tier-rank" style={{ color: tierColor }}>#{prospect.rank}</span>
            <span className="prospect-tier-label">{getTierLabel(prospect.tier)}</span>
          </div>

          <div className="prospect-modal-section-title">Measurables</div>
          <div className="prospect-modal-grid">
            <div className="prospect-modal-stat-box">
              <div className="prospect-stat-box-label">Height</div>
              <div className="prospect-stat-box-val">{prospect.height}</div>
            </div>
            <div className="prospect-modal-stat-box">
              <div className="prospect-stat-box-label">Weight</div>
              <div className="prospect-stat-box-val">{prospect.weight} lbs</div>
            </div>
            <div className="prospect-modal-stat-box">
              <div className="prospect-stat-box-label">Age</div>
              <div className="prospect-stat-box-val">{prospect.age.toFixed(1)}</div>
            </div>
          </div>

          <div className="prospect-modal-section-title">2025-26 Per Game Stats</div>
          <div className="prospect-modal-grid">
            <div className="prospect-modal-stat-box">
              <div className="prospect-stat-box-label">PPG</div>
              <div className="prospect-stat-box-val big">{prospect.pts}</div>
            </div>
            <div className="prospect-modal-stat-box">
              <div className="prospect-stat-box-label">RPG</div>
              <div className="prospect-stat-box-val big">{prospect.reb}</div>
            </div>
            <div className="prospect-modal-stat-box">
              <div className="prospect-stat-box-label">APG</div>
              <div className="prospect-stat-box-val big">{prospect.ast}</div>
            </div>
          </div>

          {prospect.scouting && (
            <>
              <div className="prospect-modal-section-title">Scouting Report</div>
              <div className="prospect-modal-scouting" style={{ borderLeftColor: tierColor }}>
                {prospect.scouting}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // --- TEAM PICKS MODAL ---
  const TeamPicksModal = ({ teamAbbr, onClose }) => {
    if (!teamAbbr) return null;
    const picks = getTeamPicks(teamAbbr);
    const teamName = TEAM_NAMES[teamAbbr] || teamAbbr;

    // Group by year
    const byYear = {};
    picks.forEach(p => {
      if (!byYear[p.year]) byYear[p.year] = [];
      byYear[p.year].push(p);
    });

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
          <button className="modal-close" onClick={onClose}>×</button>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.1em', marginBottom: 4 }}>Draft Capital</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, marginBottom: 16 }}>{teamName}</div>

          {Object.keys(byYear).sort().map(year => (
            <div key={year} style={{ marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--accent-gold)', marginBottom: 6, letterSpacing: '0.05em' }}>{year}</div>
              {byYear[year].map((pick, i) => {
                const isOwn = pick.originalTeam === teamAbbr;
                const isTraded = pick.status === 'traded';
                return (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 10px', marginBottom: 2, borderRadius: 3,
                    background: isTraded ? 'var(--accent-red-bg)' : isOwn ? 'transparent' : 'var(--accent-green-bg)',
                    opacity: isTraded ? 0.5 : 1,
                  }}>
                    <div style={{ fontSize: 12 }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, marginRight: 6 }}>Rd {pick.round}</span>
                      {!isOwn && <span style={{ color: 'var(--accent-green)', fontSize: 11 }}>via {pick.originalTeam}</span>}
                      {isOwn && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>Own pick</span>}
                    </div>
                    <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: isTraded ? 'var(--accent-red)' : 'var(--text-muted)' }}>
                      {isTraded ? `→ ${pick.tradedTo || 'Traded'}` : pick.protection || 'Unprotected'}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {picks.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '20px 0', textAlign: 'center' }}>
              No detailed pick data available for this team yet.
            </div>
          )}
        </div>
      </div>
    );
  };

  // --- BIG BOARD VIEW ---
  const BigBoardView = () => {
    let lastTier = 0;
    return (
      <div className="draft-board-list">
        {filteredProspects.map((p) => {
          const showTierHeader = p.tier !== lastTier;
          lastTier = p.tier;
          const tierColor = getTierColor(p.tier);

          return (
            <React.Fragment key={p.rank}>
              {showTierHeader && (
                <div className="draft-tier-header">
                  <div className="draft-tier-line" style={{ background: tierColor }} />
                  <span className="draft-tier-label" style={{ color: tierColor }}>{getTierLabel(p.tier)}</span>
                  <div className="draft-tier-line" style={{ background: tierColor }} />
                </div>
              )}
              <div className="draft-prospect-row" onClick={() => setSelectedProspect(p)}>
                <div className="draft-prospect-rank" style={{ color: tierColor }}>{p.rank}</div>
                <div className="draft-prospect-info">
                  <div className="draft-prospect-name">{p.name}{p.aiGenerated && <AIBadge />}</div>
                  <div className="draft-prospect-meta">{p.pos} · {p.school} · {p.cls}</div>
                </div>
                <div className="draft-prospect-phys">
                  <span>{p.height}</span>
                  <span>{p.weight}</span>
                </div>
                <div className="draft-prospect-stats">
                  <div className="draft-stat">
                    <span className="draft-stat-val">{p.pts}</span>
                    <span className="draft-stat-label">PPG</span>
                  </div>
                  <div className="draft-stat">
                    <span className="draft-stat-val">{p.reb}</span>
                    <span className="draft-stat-label">RPG</span>
                  </div>
                  <div className="draft-stat">
                    <span className="draft-stat-val">{p.ast}</span>
                    <span className="draft-stat-label">APG</span>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // --- MOCK DRAFT VIEW ---
  const MockDraftView = () => (
    <div className="draft-board-list">
      {DRAFT_ORDER_2026.map((slot) => {
        const prospect = prospects[slot.pick - 1];
        if (!prospect) return null;
        const tierColor = getTierColor(prospect.tier);

        return (
          <div key={slot.pick} className="draft-mock-row">
            <div className="draft-mock-pick">{slot.pick}</div>
            <div className="draft-mock-team">
              <div style={{ fontWeight: 600, fontSize: 13 }}>{slot.team}</div>
              {slot.via && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>via {slot.via}</div>}
            </div>
            <div className="draft-mock-arrow">→</div>
            <div className="draft-prospect-row draft-mock-prospect" onClick={() => setSelectedProspect(prospect)}>
              <div className="draft-prospect-rank" style={{ color: tierColor }}>{prospect.rank}</div>
              <div className="draft-prospect-info">
                <div className="draft-prospect-name">{prospect.name}{prospect.aiGenerated && <AIBadge />}</div>
                <div className="draft-prospect-meta">{prospect.pos} · {prospect.school}</div>
              </div>
              <div className="draft-prospect-stats">
                <div className="draft-stat">
                  <span className="draft-stat-val">{prospect.pts}</span>
                  <span className="draft-stat-label">PPG</span>
                </div>
                <div className="draft-stat">
                  <span className="draft-stat-val">{prospect.reb}</span>
                  <span className="draft-stat-label">RPG</span>
                </div>
                <div className="draft-stat">
                  <span className="draft-stat-val">{prospect.ast}</span>
                  <span className="draft-stat-label">APG</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // --- TEAM PICK INVENTORY VIEW ---
  const allTeams = Object.keys(TEAM_NAMES).sort();
  const TeamInventoryView = () => (
    <div className="draft-team-grid">
      {allTeams.map(abbr => {
        const picks = getTeamPicks(abbr);
        const ownedPicks2026 = picks.filter(p => p.year === 2026 && p.status !== 'traded');
        const tradedPicks2026 = picks.filter(p => p.year === 2026 && p.status === 'traded');
        const totalFuture = picks.filter(p => p.year > 2026 && p.status !== 'traded').length;

        return (
          <div key={abbr} className="draft-team-card" onClick={() => setSelectedTeam(abbr)}>
            <div className="draft-team-card-header">
              <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 14 }}>{abbr}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{TEAM_NAMES[abbr]}</div>
            </div>
            <div className="draft-team-card-body">
              <div className="draft-team-pick-count">
                <span className="draft-team-pick-num" style={{ color: 'var(--accent-green)' }}>{ownedPicks2026.length}</span>
                <span className="draft-team-pick-label">2026 Picks</span>
              </div>
              {tradedPicks2026.length > 0 && (
                <div className="draft-team-pick-count">
                  <span className="draft-team-pick-num" style={{ color: 'var(--accent-red)' }}>{tradedPicks2026.length}</span>
                  <span className="draft-team-pick-label">Traded Away</span>
                </div>
              )}
              <div className="draft-team-pick-count">
                <span className="draft-team-pick-num" style={{ color: 'var(--accent-gold)' }}>{totalFuture}</span>
                <span className="draft-team-pick-label">Future</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="fade-in">
      <div className="section-header">
        <div className="section-label">2026 NBA Draft</div>
        <div className="section-title">{draftYear} Draft Board</div>
        <div className="section-desc">The Darryn Peterson class — 60 prospects ranked by Tankathon consensus. Click any prospect for scouting details.</div>
      </div>

      {/* View Toggle */}
      <div className="draft-view-toggle">
        <button className={`draft-view-btn ${view === 'board' ? 'active' : ''}`} onClick={() => setView('board')}>Big Board</button>
        <button className={`draft-view-btn ${view === 'mock' ? 'active' : ''}`} onClick={() => setView('mock')}>Mock Draft</button>
        <button className={`draft-view-btn ${view === 'team' ? 'active' : ''}`} onClick={() => setView('team')}>Team Picks</button>
      </div>

      {/* Filters (board view only) */}
      {view === 'board' && (
        <div className="draft-filters">
          <input
            className="draft-search"
            type="text"
            placeholder="Search prospects..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <div className="draft-pos-filters">
            {positions.map(pos => (
              <button
                key={pos}
                className={`draft-pos-btn ${posFilter === pos ? 'active' : ''}`}
                onClick={() => setPosFilter(pos)}
              >
                {pos}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      {view === 'board' && <BigBoardView />}
      {view === 'mock' && <MockDraftView />}
      {view === 'team' && <TeamInventoryView />}

      {/* Modals */}
      {selectedProspect && <ProspectModal prospect={selectedProspect} onClose={() => setSelectedProspect(null)} />}
      {selectedTeam && <TeamPicksModal teamAbbr={selectedTeam} onClose={() => setSelectedTeam(null)} />}
    </div>
  );
};

export default DraftBoard;
